import { Users, Search, Plus, ChevronDown, ChevronUp, Users2, Users2Icon, LucideUsers2, LucideUserPlus2, LucideUserCog, LucideUserCog2 } from "lucide-react";
import DataTable from "react-data-table-component";
import { motion } from "framer-motion";
import { useState } from "react";

export default function PublishersTable({ data, columns, openModal }) {
  const [searchText, setSearchText] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");

  const handleSearchChange = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  const filteredData = (data || []).filter((item) =>
    item.name?.toLowerCase().includes(searchText) ||
    item.email?.toLowerCase().includes(searchText) ||
    item.phone?.toLowerCase().includes(searchText) ||
    item.status?.toLowerCase().includes(searchText) ||
    item.company?.toLowerCase().includes(searchText)
  );

  const customStyles = {
    table: {
      style: {
        minWidth: "1000px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        minHeight: "60px",
        backgroundColor: "transparent",
        borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
        "&:hover": {
          backgroundColor: "rgba(99, 102, 241, 0.05)",
        },
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        backgroundColor: "rgba(249, 250, 251, 0.8)",
        backdropFilter: "blur(4px)",
        fontSize: "0.875rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#4b5563",
      },
    },
    cells: {
      style: {
        fontSize: "0.875rem",
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative bg-white/90 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      {/* Decorative gradient elements */}
      <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-indigo-100/30 filter blur-3xl -z-0" />
      <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-purple-100/20 filter blur-2xl -z-0" />

      <div className="relative z-10 space-y-6">
        {/* Header */}
        <motion.div
          className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-3">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-xl md:text-2xl lg:text-3xl font-extrabold flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-gradient-x drop-shadow-sm"
            >
              <LucideUserCog2 className="w-6 h-6 text-pink-500 animate-bounce-slow" />
              Manage Publisher
            </motion.h2>
          </div>

          <motion.button
            onClick={openModal}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:brightness-110 transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>New Publisher</span>
          </motion.button>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="relative w-full sm:w-1/2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search publishers..."
              value={searchText}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
              Filter
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
            >
              Sort
              {sortDirection === "asc" ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </motion.div>

        {/* Table Container */}
        <motion.div
          className="w-full overflow-x-auto border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="min-w-[900px]">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50]}
              highlightOnHover
              fixedHeader
              fixedHeaderScrollHeight="500px"
              responsive={true}
              customStyles={customStyles}
              noDataComponent={
                <div className="p-8 text-center text-gray-500">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium">No publishers found</h3>
                  <p className="mt-1">Try adjusting your search or filters</p>
                </div>
              }
            />
          </div>
        </motion.div>

        {/* Status Bar */}
        <motion.div
          className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div>
            Showing {filteredData.length} of {data?.length || 0} publishers
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}